import React, { useLayoutEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import clsx from 'clsx'
import { groupBy, sortBy, take } from 'lib/array/arrayUtils'

import Heading from 'LuxKit/Typography/Heading'
import { useInView } from 'react-intersection-observer'
import useMountedEffect from 'hooks/useMountedEffect'
import Group from 'components/utils/Group'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import TextLink from 'LuxKit/TextLink'
import AccommodationCalendarDayMonth, { AccommodationDayPickerSize } from './AccommodationCalendarDayMonth'
import FormatDateTime from 'components/utils/Formatters/FormatDateTime'
import { AccommodationMobileDayCellSelectHandler } from './AccommodationCalendarSmallDayCell'
import BodyText from 'LuxKit/Typography/BodyText'
import { rem } from 'polished'
import { mediaHoverable } from 'lib/theme/mediaQueries'

export interface AccommodationDayView<T = any> {
  /**
   * The date in an ISO string representing this day
   */
  date: string
  /**
   * The year this view is for
   */
  year: number
  /**
   * The month this view is for, indexed from 0.
   */
  month: number
  /**
   * The day this view is for, indexed from 0
   */
  day: number
  /**
   * The pricing data for this month
   */
  pricing?: App.Pricing
  /**
   * Whether or not it is sold out
   */
  soldOut?: boolean
  /**
   * What type of pricing data is being used
   * This will influence the display of the pricing
   */
  priceType: 'default' | 'member'
  /**
   * The number of items currently being purchased
   * @example Purchasing 2 rooms, the unit count would be 2
   */
  itemCount?: number
  /**
   * The unit of the item being purchased
   * @example Purchasing 2 rooms, the item unit would be room
   */
  itemUnit?: string
  /**
   * How long the item being purchase will goes for
   */
  duration?: number
  /**
   * The unit representing how long it will go for
   */
  durationUnit: 'day' | 'night'
  /**
   * Label that will apear in top right hand corner of month.
   * Ensure only Label components are used
   */
  label?: React.ReactNode
  /**
   * This month is a 'featured' day, for whatever reason.
   * Will apply the flame icon next to the day.
   */
  featured?: 'special' | 'loyalty'
  /**
   * Text that will appear when it's sold out
   * indicating that there is another action the user can take
   * Please handle this extra action manually with the on select
   */
  soldOutExtraActionText?: string
  /**
   * Whether or not the price is conditional, this will add a '*' to it
   */
  conditionalPrice?: boolean
  /**
   * This provides the ability to augment a view with additional data
   * you may want to have carried around to events and other things
   * @default undefined
   */
  additionalData?: T
}

interface AccommodationMonthView {
  year: number
  month: number
  date: Date
  days: Array<AccommodationDayView>
}

const CalendarGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7 , 1fr);

  > .full-width {
    grid-column: 1 / -1;
  }

  ${mediaHoverable} {
    &.enable-hover {
      > .type-check-in ~ .type-none:has(~ .type-none:hover), > .type-check-in ~ .type-none:hover {
        background-color: ${props => props.theme.palette.neutral.default.seven};
      }
    }
  }
`

const Gap20 = styled.div`
  height: ${rem(20)};
  grid-column: 1 / -1;
`

interface Props {
  views: Array<AccommodationDayView>
  size: AccommodationDayPickerSize
  className?: string
  onBackToMonth?: () => void
  initialYear?: number
  initialMonth?: number
  contentRef: React.RefObject<HTMLElement | null>
  startDate?: string
  endDate?: string
  onDaySelect?: AccommodationMobileDayCellSelectHandler
}

function AccommodationCalendarDayPicker(props: Props) {
  const {
    views,
    className,
    initialYear,
    initialMonth,
    contentRef,
    onBackToMonth,
    startDate,
    endDate,
    onDaySelect,
    size,
  } = props

  // always show month selected, previous month and next month
  const viewsByMonth = useMemo(() => {
    return sortBy(
      groupBy(views, view => `${view.year}-${view.month}`)
        .values()
        .map((viewGroup): AccommodationMonthView => ({
          date: new Date(viewGroup[0].date),
          month: viewGroup[0].month,
          year: viewGroup[0].year,
          days: viewGroup,
        })),
      group => group.date,
      'asc',
    )
  }, [views])

  const [monthsOnDisplay, setMonthsOnDisplay] = useState(() => {
    if (initialYear !== undefined && initialMonth !== undefined) {
      const index = viewsByMonth.findIndex(view => view.month === initialMonth && view.year === initialYear)
      return { toShow: 3, offset: Math.max(index - 1, 0) }
    }
    return { toShow: 3, offset: 0 }
  })

  const [previousRef, previousInView] = useInView({ root: contentRef.current, rootMargin: '20px 0px 0px 0px' })
  const [nextRef, nextInView] = useInView({ root: contentRef.current, rootMargin: '0px 0px 100px 0px' })
  const monthElements = useRef<Record<string, HTMLDivElement | null>>({})
  const scrollRestoreMonth = useRef<typeof monthsOnDisplay | null>(null)

  useLayoutEffect(() => {
    if (monthsOnDisplay.offset > 0) {
      // we render 3 months (previous/current/next), but we want the scroll
      // to our "initial" month so it's at the top
      const month = viewsByMonth[monthsOnDisplay.offset + 1]
      const element = monthElements.current[+month.date]
      if (element) {
        element.scrollIntoView({ behavior: 'auto' })
      }
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useMountedEffect(() => {
    if (previousInView) {
      // user scrolled to the top, so we show another month at the start
      scrollRestoreMonth.current = monthsOnDisplay
      setMonthsOnDisplay({ toShow: monthsOnDisplay.toShow + 1, offset: monthsOnDisplay.offset - 1 })
    }
  }, [previousInView])

  useLayoutEffect(() => {
    // this handles when we scroll up and a new month appears
    // we want the user to not be at the top, but stay in the 'same position' before
    // the new month came in
    if (scrollRestoreMonth.current !== undefined) {
      const month = viewsByMonth[monthsOnDisplay.offset + 1]
      const element = monthElements.current[+month.date]
      if (element) {
        contentRef.current?.scrollTo({
          behavior: 'auto',
          top: element.offsetHeight + 40,
        })
      }
      scrollRestoreMonth.current = null
    }
  }, [monthsOnDisplay.offset]) // eslint-disable-line react-hooks/exhaustive-deps

  useLayoutEffect(() => {
    if (nextInView) {
      // scrolled to the bottom of our current set of months - add one more month to the bottom
      setMonthsOnDisplay(monthsOnDisplay => ({ ...monthsOnDisplay, toShow: monthsOnDisplay.toShow + 1 }))
    }
  }, [nextInView])

  return (<VerticalSpacer className={className} gap={20}>
    {monthsOnDisplay.offset > 0 && <div ref={previousRef} />}
    {/*
      We use a single grid container for every calendar and their content as this allows
      us to apply styles (such as hover) via css sibling selectors across months
      muuuch more performant than trying to do it in javascript
      It does make the styling a little more fragile, so be aware of this.
    */}
    <CalendarGrid className={clsx({ 'enable-hover': !endDate })}>
      {take(viewsByMonth, monthsOnDisplay.toShow, monthsOnDisplay.offset).map((view) => {
        return <React.Fragment key={+view.date}>
          {size === 'small' && <VerticalSpacer
            gap={4}
            className="full-width"
            ref={(el) => { monthElements.current[+view.date] = el }}
          >
            <Group direction="horizontal" horizontalAlign="space-between">
              <Heading variant="heading5">
                <FormatDateTime value={view.date} format="long-month" />
              </Heading>
              {onBackToMonth && <TextLink size="large" onClick={onBackToMonth}>{view.year}</TextLink>}
              {!onBackToMonth && <BodyText variant="large" weight="bold">{view.year}</BodyText>}
            </Group>
          </VerticalSpacer>}
          {size === 'large' && <Heading
            variant="heading5"
            className="full-width"
            ref={(el) => { monthElements.current[+view.date] = el }}
          >
            <FormatDateTime value={view.date} format="long-month-long-year" />
          </Heading>}
          <Gap20 />
          <AccommodationCalendarDayMonth
            days={view.days}
            startDate={startDate}
            endDate={endDate}
            onDaySelect={onDaySelect}
            size={size}
          />
          <Gap20 />
        </React.Fragment>
      })}
    </CalendarGrid>
    {(monthsOnDisplay.toShow + monthsOnDisplay.offset) < views.length && <div ref={nextRef} />}
  </VerticalSpacer>)
}

export default React.memo(AccommodationCalendarDayPicker)
